<template>
  <div>
    <div
      v-for="(expense, expenseIndex) in currentPoliticalAction.memory.expenses"
      :key="expenseIndex"
    >
      <div
        class="flex justify-between border-bottom border-blue w-full h-fit mt-6"
      >
        <!-- 経費 -->
        <span class="text-blue text-lg font-bold">
          経費{{ expenseIndex + 1 }}/{{
            currentPoliticalAction.memory.expenses.length
          }}
        </span>
        <!-- 総支出 -->
        <span class="flex flex-row text-blue">
          <div>
            <span>{{ makeProratedText(expense) }}</span>
            <span class="mx-0.5">/</span>
            <span class="h-fit text-lg font-bold">総</span>
            <span class="text-sm font-bold">支出</span>
            <span class="text-lg font-bold">{{ totalExpensesText }}</span>
          </div>
        </span>
      </div>
      <app-table v-bind="$attrs" class="ml-3 mr-3 w-full">
        <template v-slot:table-body>
          <tr
            class="border border-collapse border-dashed border-blue border-t-0 border-l-0 border-r-0 h-12"
            style="height:49px; width:848px"
            v-for="(header, index) in getRejectHeaders(expenseIndex + 1)"
            :key="index"
          >
            <template v-if="rejected(header.attr)">
              <td width="185px" class="rejected-line">
                <div class="flex flex-row items-center cursor-pointer">
                  <RejectedIcn width="20px" height="20px" class="ml-3 mr-1" />
                  <span class="text-lg text-red font-bold">
                    {{ header.title }}
                  </span>
                </div>
                <div
                  class="flex flex-row items-center cursor-pointer mt-4"
                  @click="rejectToggleAttr(header)"
                >
                  <RejectCancelIcn class="ml-3 mr-1" />
                  <div class="flex flex-row items-baseline">
                    <span class="text-sm text-red font-bold">
                      差戻し
                    </span>
                    <span class="text-lg text-red font-bold">
                      解除
                    </span>
                  </div>
                </div>
              </td>
              <td class="rejected-line pr-3" style="height: 93px">
                <span
                  class="text-lg text-red block"
                  :id="`reason-comment-${header.attr}`"
                  @click="showReasonInput(header.attr)"
                  v-show="false"
                >
                  {{
                    header.comment.length
                      ? header.comment
                      : '※ コメントはありません'
                  }}
                </span>
                <AppTextInput
                  color="red"
                  class="text-red"
                  placeholder="コメントを入力ください"
                  v-model="header.comment"
                  @blur="onBlur(header)"
                  :id="`reason-input-${header.attr}`"
                  v-show="true"
                />
                <!-- 差し戻しを押したときの差し戻し入力欄の下に出てくるテキスト -->
                <span v-if="header.title === '支出内容'" class="text-lg">
                  {{ expense.receipt?.expenseContent1 }}<br />
                  <span v-if="expense.receipt?.expenseContent2">
                    {{ expense.receipt?.expenseContent2 }}
                  </span>
                </span>
                <span v-else class="text-lg">
                  {{ getExpenseData(header.title, expense) }}
                </span>
              </td>
            </template>
            <template v-else>
              <td width="185px">
                <div
                  class="flex flex-row items-center cursor-pointer"
                  @click="rejectToggleAttr(header)"
                >
                  <RejectIcn width="20px" height="20px" class="ml-3 mr-1" />
                  <span class="text-lg text-blue font-bold">
                    {{ header.title }}
                  </span>
                </div>
              </td>
              <td v-if="header.title === '支出内容'">
                <span class="text-lg">
                  {{ expense.receipt?.expenseContent1 }}<br />
                  <span v-if="expense.receipt?.expenseContent2">
                    {{ expense.receipt?.expenseContent2 }}
                  </span>
                </span>
              </td>
              <td v-else-if="header.title === '領収書'">
                <span class="text-lg">
                  <div
                    v-for="(img, index) in expense.receipt.images"
                    :key="index"
                  >
                    <span :class="`text-red mb-1 ml-1`"
                      >※画像クリックで黒塗りダイアログが開きます</span
                    >
                    <img
                      v-if="img.normalImage"
                      :src="
                        img.blackImage == null
                          ? img.normalImage
                          : img.blackImage
                      "
                      @click="
                        openImgPreview('memory', expenseIndex, {
                          normalImage: img.normalImage,
                          blackImage: img.blackImage,
                        })
                      "
                    />
                  </div>
                </span>
              </td>
              <td v-else>
                <span class="text-lg">
                  {{ getExpenseData(header.title, expense) }}
                </span>
              </td>
            </template>
          </tr>
        </template>
      </app-table>
    </div>
  </div>
</template>

<script>
import { nextTick, reactive, computed, getCurrentInstance } from 'vue'

import AppTable from '@/components/ui/AppTable'
import AppTextInput from '@/components/ui/AppTextInput'
import RejectIcn from '@/assets/icons/reject.svg?inline'
import RejectedIcn from '@/assets/icons/rejected.svg?inline'
import RejectCancelIcn from '@/assets/icons/reject-cancel.svg?inline'
import { usePoliticalActions } from '@/services/politicalAction/usePoliticalActions'
import { useMemoryReject } from '@/services/politicalAction/useMemoryReject'
import { useDialog } from '@/services/useDialog'

export default {
  name: 'MemoryOffice',

  components: {
    AppTable,
    AppTextInput,
    RejectIcn,
    RejectedIcn,
    RejectCancelIcn,
  },

  setup() {
    let { setRejectHeaders, rejectHeaders, rejectedAttrs } = useMemoryReject()

    let imgPreviewState = reactive({ isShown: false, previewImgSrc: null })

    const { currentPoliticalAction } = usePoliticalActions()

    const isShowImgPreview = src => {
      imgPreviewState.isShown = true
      imgPreviewState.previewImgSrc = src
    }

    const onBlur = header => {
      let comment = document.getElementById(`reason-comment-${header.attr}`)
      let reasonInput = document.getElementById(`reason-input-${header.attr}`)
      comment.style.display = 'block'
      reasonInput.style.display = 'none'
      header.comment = reasonInput.value
      rejectedAttrs.find(item => item.target == header.attr).comment =
        reasonInput.value
    }

    const rejectToggleAttr = header => {
      const itemIndex = rejectedAttrs.findIndex(
        item => item.target == header.attr,
      )

      if (itemIndex > -1) {
        rejectedAttrs.splice(itemIndex, 1)
        header.comment = ''
        // headers.find((item) => item.attr == attr).comment = "";
      } else {
        rejectedAttrs.push({
          target: header.attr,
          comment: '',
        })
        nextTick(() => {
          document.getElementById(`reason-input-${header.attr}`).focus()
        })
      }
    }

    const rejected = attr => {
      return rejectedAttrs.findIndex(item => item.target == attr) > -1
    }

    const showReasonInput = attr => {
      let comment = document.getElementById(`reason-comment-${attr}`)
      let reasonInput = document.getElementById(`reason-input-${attr}`)
      comment.style.display = 'none'
      reasonInput.style.display = 'block'
      reasonInput.focus()
    }

    const titleMap = new Map([
      // 電車
      [
        1,
        [
          '項目種別',
          '経路',
          '人数',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
      // 自家用車
      [
        2,
        [
          '項目種別',
          '経路',
          '距離',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
      // 飛行機
      [
        3,
        [
          '項目種別',
          '経路',
          '人数',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
      // 宿泊費
      [
        4,
        [
          '項目種別',
          '宿泊詳細',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
      // パック旅行
      [
        5,
        [
          '項目種別',
          '宿泊人数/日数',
          '1人あたりの料金',
          '明細',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
      // その他
      [
        6,
        [
          '項目種別',
          '費目',
          '明細',
          '経費項目',
          '支払額',
          '支出年月日',
          '支出内容',
          '領収書',
        ],
      ],
    ])

    const attrMap = new Map([
      // 電車
      [
        1,
        [
          'type',
          'transportation',
          'people',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
      // 自家用車
      [
        2,
        [
          'type',
          'transportation',
          'distance',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
      // 飛行機
      [
        3,
        [
          'type',
          'transportation',
          'people',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
      // 宿泊費
      [
        4,
        [
          'type',
          'accommodationDetails',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
      // パック旅行
      [
        5,
        [
          'type',
          'people/days',
          'price',
          'detail',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
      // その他
      [
        6,
        [
          'type',
          'item',
          'detail',
          'expenseType',
          'expenseAmount',
          'expenseDate',
          'expenseContent',
          'file',
        ],
      ],
    ])

    const rejectHeadersData = expenses => {
      expenses.forEach((expense, expenseIndex) => {
        // 電車、自家用車、飛行機、etc...
        const expenseTypeId = expense.type[0].id
        attrMap.get(expenseTypeId).forEach((attr, attrIndex) => {
          // attrにIDを付けたrejectHeaderを作成
          setRejectHeaders.push({
            title: titleMap.get(expenseTypeId)[attrIndex],
            attr: attr + (expenseIndex + 1),
            comment: '',
          })
        })
      })
    }

    const getRejectHeaders = id => {
      if (rejectHeaders.length === 0)
        rejectHeadersData(currentPoliticalAction.memory.expenses)
      return rejectHeaders.filter(rejectHeader =>
        rejectHeader.attr.includes(id),
      )
    }

    const makeProratedText = expense => {
      const amount = expense?.receipt?.expenseAmount ?? 0
      const numerator = expense?.receipt?.numerator ?? 0
      const denominator = expense?.receipt?.denominator ?? 0
      let proratedAmount = amount
      if (!(amount === 0 || numerator === 0 || denominator === 0)) {
        proratedAmount = prorate(amount, numerator, denominator)
      }
      return `${proratedAmount.toLocaleString()}円`
    }

    const expenses = computed(() => {
      return currentPoliticalAction.memory.expenses
    })

    const totalExpenses = computed(() => {
      return expenses.value.reduce((total, expense) => {
        const amount = expense?.receipt?.expenseAmount ?? 0
        const numerator = expense?.receipt?.numerator ?? 0
        const denominator = expense?.receipt?.denominator ?? 0
        if (amount === 0) {
          return total
        } else if (numerator === 0 || denominator === 0) {
          return total + amount
        } else {
          return total + prorate(amount, numerator, denominator)
        }
      }, 0)
    })

    const totalExpensesText = computed(() => {
      return `${totalExpenses.value.toLocaleString()}円`
    })

    const makeTransportationText = expense => {
      const from = expense?.from ?? ''
      const to = expense?.to ?? ''
      const type = expense?.type ?? []
      const isStation = type.length >= 1 ? type[0].id === 1 : false
      const isAirport = type.length >= 1 ? type[0].id === 3 : false
      const isCar = type.length >= 1 ? type[0].id === 2 : false
      if (!(isStation || isAirport || isCar)) {
        console.debug(`expense from or to is invalid. to: ${to}, from: ${from}`)
        return ''
      }
      let location = ''
      if (isStation) {
        location = '駅'
      } else if (isAirport) {
        location = '空港'
      }
      const roundTrip = expense.isRoundTrip ? ' [往復]' : ''
      return `${from}${location} 〜 ${to}${location}${roundTrip}`
    }

    const makeStayingText = expense => {
      const people = expense?.people ?? 0
      const stayingDays = expense?.stay_days ?? 0
      return `${people}人 / ${stayingDays}泊`
    }

    const prorate = (amount, numerator, denominator) => {
      return Math.floor((numerator * amount) / denominator)
    }

    const makeAmountText = (amount, numerator = null, denominator = null) => {
      amount = amount ?? 0
      const amountWithComma = amount.toLocaleString()
      if (
        numerator === null ||
        denominator === null ||
        typeof numerator !== 'number' ||
        typeof denominator !== 'number' ||
        numerator < 1 ||
        denominator < 1
      ) {
        return `${amountWithComma}円`
      }

      const proratedAmount = prorate(amount, numerator, denominator)
      const proratedStr = proratedAmount.toLocaleString()
      return `${amountWithComma}円 [${proratedStr}円 按分 ${numerator}/${denominator}]`
    }

    const expenseTypeIdToTitleMap = new Map([
      [1, '調査研究費'],
      [2, '研修費用'],
      [3, '広報費'],
      [4, '公聴費'],
      [5, '要請・陳情活動費'],
      [6, '会議費'],
      [7, '資料作成費'],
      [8, '資料購入費'],
      [9, '人件費'],
      [10, '事務所費'],
    ])

    const getExpenseTypeName = expenseTypeId => {
      if (!expenseTypeId) {
        return ''
      }
      if (
        !(
          typeof expenseTypeId === 'number' &&
          expenseTypeId >= 1 &&
          expenseTypeId <= 10
        )
      ) {
        throw new Error('expense_type_id is invalid: ' + expenseTypeId)
      }
      return expenseTypeIdToTitleMap.get(expenseTypeId) ?? ''
    }

    const makePaymentDateText = expenseDate => {
      if (typeof expenseDate !== 'string') {
        throw new Error('receipt expense date is invalid: ' + expenseDate)
      }
      const app = getCurrentInstance()
      const $filters = app.appContext.config.globalProperties.$filters
      const yearText = $filters.dateFormat(expenseDate)
      const wareki = $filters.wareki(expenseDate)
      return `${yearText} | ${wareki}`
    }

    const getExpenseData = (title, expense) => {
      const expenseData =
        title === '項目種別'
          ? expense.type[0].name
          : title === '経路'
          ? makeTransportationText(expense)
          : title === '人数'
          ? expense.people + '人'
          : title === '距離'
          ? expense.distance + 'Km'
          : title === '宿泊人数/日数'
          ? makeStayingText(expense)
          : title === '1人あたりの料金'
          ? makeAmountText(expense?.amount)
          : title === '明細'
          ? expense.detail
          : title === '費目'
          ? expense.item
          : title === '宿泊詳細'
          ? expense.detail
          : title === '経費項目'
          ? getExpenseTypeName(expense.receipt?.expenseTypeId)
          : title === '支払額'
          ? makeAmountText(
              expense.receipt?.expenseAmount,
              expense.receipt?.numerator,
              expense.receipt?.denominator,
            )
          : title === '支出年月日'
          ? makePaymentDateText(expense.receipt.expenseDate)
          : ''
      return expenseData
    }

    return {
      rejectHeaders,
      currentPoliticalAction,
      rejectedAttrs,
      rejected,
      rejectToggleAttr,
      showReasonInput,
      onBlur,
      isShowImgPreview,
      imgPreviewState,
      ...useDialog(),
      makeProratedText,
      totalExpensesText,
      getRejectHeaders,
      getExpenseData,
    }
  },
}
</script>

<style lang="scss" scoped>
.rejected-line {
  background: theme('colors.red.lightest');
}
td:first-child {
  border-top-left-radius: 22px;
  border-bottom-left-radius: 22px;
}
td:last-child {
  border-top-right-radius: 22px;
  border-bottom-right-radius: 22px;
}
</style>
